import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

const PDF_TYPES = ['invoice', 'certificate'];

interface PreviewVariant {
    label: string;
    subject: string;
    html: string;
}

interface DefaultPreview {
    format: 'html' | 'none';
    variants: PreviewVariant[];
}

interface DefaultPreviewDialogProps {
    open: boolean;
    purpose: { slug: string; name: string; type: string } | null;
    onClose: () => void;
}

export const DefaultPreviewDialog: React.FC<DefaultPreviewDialogProps> = ({ open, purpose, onClose }) => {
    const [preview, setPreview] = useState<DefaultPreview | null>(null);
    const [pdfUrl, setPdfUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    const slug = purpose?.slug;
    const isPdf = !!purpose && PDF_TYPES.includes(purpose.type);

    useEffect(() => {
        if (!open || !slug) return;

        let cancelled = false;
        let objectUrl: string | null = null;
        setPreview(null);
        setPdfUrl(null);
        setFailed(false);

        (async () => {
            try {
                const response = await fetch(`${API_URL}/document-templates/purposes/${slug}/default-preview`, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
                });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }

                if (isPdf) {
                    const blob = await response.blob();
                    if (cancelled) return;
                    objectUrl = URL.createObjectURL(blob);
                    setPdfUrl(objectUrl);
                } else {
                    const data = await response.json();
                    if (!cancelled) setPreview(data);
                }
            } catch (err) {
                console.error('Error loading default preview:', err);
                if (!cancelled) setFailed(true);
            }
        })();

        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [open, slug, isPdf]);

    const renderContent = () => {
        if (failed) {
            return <Alert severity="error">Standardvorlage konnte nicht geladen werden</Alert>;
        }
        if (pdfUrl) {
            return (
                <iframe
                    src={pdfUrl}
                    title="Standardvorlage"
                    style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
                />
            );
        }
        if (!preview) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }
        if (preview.format === 'none') {
            return (
                <Alert severity="info">
                    Für diesen Zweck gibt es keinen Standardtext. Die E-Mail wird nur verschickt, wenn Sie eine eigene Vorlage zuweisen.
                </Alert>
            );
        }
        const several = preview.variants.length > 1;
        return preview.variants.map((variant) => (
            <Box key={variant.label} sx={{ mb: 3, '&:last-child': { mb: 0 } }}>
                {several && (
                    <Typography variant="subtitle1" component="h3" fontWeight="bold" gutterBottom>
                        {variant.label}
                    </Typography>
                )}
                <Typography variant="body2" sx={{ mb: 1 }}>
                    Betreff: {variant.subject}
                </Typography>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: '#fff' }}>
                    <iframe
                        sandbox=""
                        srcDoc={variant.html}
                        title={several ? `Standardvorlage – ${variant.label}` : 'Standardvorlage'}
                        style={{ width: '100%', height: '50vh', border: 'none', display: 'block' }}
                    />
                </Box>
            </Box>
        ));
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Standardvorlage: {purpose?.name}</DialogTitle>
            <DialogContent>{renderContent()}</DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Schließen</Button>
            </DialogActions>
        </Dialog>
    );
};
